import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Text,
  useToast,
  VStack,
  HStack,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useState, useCallback } from "react";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useTrackingTrips, type Trip } from "hooks/useTrackingTrips";
import type { TripStatus } from "services/tracking/tripsService";
import { usePermissions } from "hooks/usePermissions";
import { showToast, toastTypes } from "utils";

const statusColors: Record<string, string> = {
  created: "gray",
  started: "blue",
  finished: "green",
};

function formatDate(dateString: string | null) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("fr-FR");
}

export default function TrackingDashboardPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [statusFilter, setStatusFilter] = useState<TripStatus | "">("");
  const params = statusFilter ? { status: statusFilter as TripStatus } : undefined;
  const { trips, isLoading, error, refetch, stopTrip } = useTrackingTrips(params);
  const [stoppingId, setStoppingId] = useState<string | null>(null);

  const handleStopTrip = useCallback(
    async (tripId: string) => {
      setStoppingId(tripId);
      try {
        await stopTrip(tripId);
        showToast({ toast, message: "Trajet arrêté avec succès", status: toastTypes.success });
      } catch (e: any) {
        const message = e?.response?.data?.message || e?.message || "Erreur lors de l'arrêt du trajet";
        showToast({ toast, message, status: toastTypes.error });
      } finally {
        setStoppingId(null);
      }
    },
    [stopTrip, toast]
  );

  const handleViewHistory = useCallback(
    (tripId: string) => {
      navigate(`/tracking/trip/${tripId}/history`);
    },
    [navigate]
  );

  const handleViewLiveMap = useCallback(
    (tripId: string) => {
      navigate(`/tracking/trip/${tripId}/live`);
    },
    [navigate]
  );

  return (
    <TrackingPageLayout
      title="Tracking – Trajets"
      withWhiteBox={false}
      action={
        <HStack spacing={2} flexWrap="wrap">
          <Button size="sm" variant="ghost" onClick={() => navigate("/tracking/profile")}>
            Mon profil
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate("/tracking/notifications")}>
            Notifications
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate("/tracking/addresses")}>
            Adresses
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate("/tracking/parent")}>
            Mes élèves
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate("/tracking/guard")}>
            Guard
          </Button>
          {has("get tracking_students") && (
            <Button size="sm" variant="outline" onClick={() => navigate("/tracking/students")}>
              Élèves (admin)
            </Button>
          )}
          <Select
            placeholder="Tous les statuts"
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value || "") as TripStatus | "")}
            w="180px"
          >
            <option value="created">Créé</option>
            <option value="started">En cours</option>
            <option value="finished">Terminé</option>
          </Select>
          <Button onClick={() => refetch()} isLoading={isLoading} size="md">
            Actualiser
          </Button>
        </HStack>
      }
    >
      {error && (
        <Box mb={4} p={4} bg="red.50" borderRadius="md">
          <Text color="red.700">
            Erreur lors du chargement des trajets. Vérifiez que l'API Tracking est disponible et que vous êtes connecté.
          </Text>
          <Button mt={2} size="sm" onClick={() => refetch()}>
            Réessayer
          </Button>
        </Box>
      )}

      {isLoading && trips.length === 0 ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" />
        </Flex>
      ) : trips.length === 0 ? (
        <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
          <Text color="gray.600">Aucun trajet disponible.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {(trips as Trip[]).map((trip) => (
            <Box
              key={trip.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="white"
              shadow="sm"
            >
              <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
                <Box>
                  <Heading size="sm" mb={1}>
                    {trip.bus?.name} – {trip.route?.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    Plaque : {trip.bus?.plate_number || "—"}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Chauffeur : {trip.driver_username || "—"} · Début : {formatDate(trip.started_at)} · Fin : {formatDate(trip.finished_at)}
                  </Text>
                </Box>
                <Badge colorScheme={statusColors[trip.status] || "gray"} fontSize="sm">
                  {trip.status}
                </Badge>
              </Flex>
              <HStack mt={3} spacing={2}>
                {(trip.status === "finished" || trip.status === "started") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewHistory(trip.id)}
                  >
                    Voir l'historique
                  </Button>
                )}
                {trip.status === "started" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewLiveMap(trip.id)}
                  >
                    Carte temps réel
                  </Button>
                )}
                {trip.status === "started" && (
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => handleStopTrip(trip.id)}
                    isLoading={stoppingId === trip.id}
                  >
                    Arrêter le trajet
                  </Button>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </TrackingPageLayout>
  );
}
